// One WebSocket to the bridge server, dialled once: it opens, sends the
// hello, and is connected when the bridge's welcome arrives; frames before
// the welcome are not calls and are ignored. When it closes, for any reason,
// it stays closed: the user asked for no retry (2026-10-21, "there's no need
// to retry"), so the page dials again only on their word (the switch, the
// Connect button, the next load). A send while not connected is dropped.
// The pure parts, the address and the tab id, are tested; the handshake is
// tested with a fake socket.

export const DEFAULT_PORT = 7357;
export const BRIDGE_PATH = '/browser';
export const TAB_ID_KEY = 'ai-bridge.tabId';

/** `ws://127.0.0.1:<port>/browser`; a port that is not a number falls back to the default. */
export function bridgeUrl(port: string | number | undefined): string {
  const n = typeof port === 'number' ? port : port ? Number.parseInt(port, 10) : NaN;
  const chosen = Number.isInteger(n) && n > 0 && n < 65536 ? n : DEFAULT_PORT;
  return `ws://127.0.0.1:${chosen}${BRIDGE_PATH}`;
}

interface TabStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The id of this tab: generated once per page load and kept in session storage, so a reload keeps it. */
export function tabIdFrom(storage: TabStorage | undefined, random: () => string): string {
  let stored: string | null = null;
  try {
    stored = storage?.getItem(TAB_ID_KEY) ?? null;
  } catch {
    stored = null;
  }
  if (stored) return stored;
  const id = random();
  try {
    storage?.setItem(TAB_ID_KEY, id);
  } catch {
    // Private mode, or storage full: the id lives for this load only.
  }
  return id;
}

/** The first message after the socket opens. */
export interface Hello {
  type: 'hello';
  tabId: string;
  url: string;
  title: string;
}

/** True for the bridge's welcome frame, `{ "type": "welcome" }`, whatever else it carries. */
export function isWelcome(text: unknown): boolean {
  if (typeof text !== 'string') return false;
  try {
    const m: unknown = JSON.parse(text);
    return !!m && typeof m === 'object' && (m as { type?: unknown }).type === 'welcome';
  } catch {
    return false;
  }
}

/** Why the connection is not up: the user's words for the status line. */
export const REASON_NOT_RUNNING = 'the bridge is not running';
export const REASON_CLOSED_BEFORE_WELCOME = 'the bridge closed before welcoming the page';
export const REASON_WENT_AWAY = 'the bridge went away';

export interface ConnectionOptions {
  url: string;
  /** The hello to send once the socket opens. */
  hello(): Hello;
  /** Every frame's text after the welcome. */
  onMessage(text: unknown): void;
  /** The welcome arrived: the tab is connected. */
  onOpen(): void;
  /** The socket is gone, whichever way; never after `close()` was called. */
  onClose(reason: string): void;
  /** One line each: connected, disconnected. */
  debug(message: string, fields?: Record<string, unknown>): void;
  /** Overridable for a test; the browser's WebSocket by default. */
  socket?: (url: string) => WebSocketLike;
}

/** The part of a WebSocket the connection uses. */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: 'open' | 'close' | 'error' | 'message', listener: (event: { data?: unknown; code?: number; reason?: string }) => void): void;
}

export interface Connection {
  /** Sends `message` as JSON when connected; false, and nothing sent, otherwise. */
  send(message: unknown): boolean;
  /** Closes the socket; `onClose` is not called for this. */
  close(): void;
  /** True from the welcome until the socket closes. */
  readonly open: boolean;
}

const OPEN = 1;

export function connect(options: ConnectionOptions): Connection {
  const make = options.socket ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike);
  let socket: WebSocketLike | undefined;
  let phase: 'dialling' | 'welcoming' | 'connected' | 'closed' = 'dialling';
  let stopped = false;

  const finish = (reason: string) => {
    if (phase === 'closed') return;
    const wasConnected = phase === 'connected';
    phase = 'closed';
    socket = undefined;
    if (wasConnected) options.debug('disconnected from the bridge', { url: options.url, reason });
    if (!stopped) options.onClose(reason);
  };

  try {
    socket = make(options.url);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    queueMicrotask(() => finish(`could not open a socket: ${message}`));
    return { send: () => false, close: () => void (stopped = true), open: false };
  }

  const s = socket;
  s.addEventListener('open', () => {
    if (socket !== s || phase !== 'dialling') return;
    phase = 'welcoming';
    try {
      s.send(JSON.stringify(options.hello()));
    } catch {
      // The close that follows reports it.
    }
  });
  s.addEventListener('message', (event) => {
    if (socket !== s) return;
    if (phase === 'connected') {
      options.onMessage(event.data);
      return;
    }
    if (phase !== 'welcoming' || !isWelcome(event.data)) return;
    phase = 'connected';
    options.debug('connected to the bridge', { url: options.url });
    options.onOpen();
  });
  s.addEventListener('error', () => {
    // A close event follows every error; nothing to do here.
  });
  s.addEventListener('close', (event) => {
    if (socket !== s) return;
    const detail = event.reason ? `: ${event.reason}` : event.code ? ` (code ${event.code})` : '';
    const reason = phase === 'dialling' ? REASON_NOT_RUNNING : phase === 'connected' ? `${REASON_WENT_AWAY}${detail}` : `${REASON_CLOSED_BEFORE_WELCOME}${detail}`;
    finish(reason);
  });

  return {
    send(message) {
      if (phase !== 'connected' || !socket || socket.readyState !== OPEN) return false;
      try {
        socket.send(JSON.stringify(message));
        return true;
      } catch {
        return false;
      }
    },
    close() {
      stopped = true;
      const current = socket;
      phase = 'closed';
      socket = undefined;
      try {
        current?.close();
      } catch {
        // Closed already.
      }
    },
    get open() {
      return phase === 'connected' && !!socket && socket.readyState === OPEN;
    },
  };
}
